import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { createAppAsyncThunk } from "appThunk";
import { apiGet, ApiResponseStatus } from "modules/helpers/api/apiSlice";
import { RootState } from "store";

interface FeatureFlags {
    enableCustomerToolsLocationDesktop: boolean,
    enableCustomerToolsLocationMobile: boolean,
    enableCustomerInsightsPortfolioAreaHealthNew: boolean,
    enableCustomerInsightsPortfolioNearbyCompetitionNew: boolean,
    enableCustomerInsightsPortfolioCustomCalendar: boolean,
    enableCustomerInsightsPortfolioFootfallNew: boolean,
    enableCustomerInsightsPortfolioNew: boolean,
    enableCustomerInsightsCost: boolean,
    enableCustomerInsightsCostCustomCalendar: boolean,
    enableCustomerHighchartsExporting: boolean,
    enableCustomerMapboxExporting: boolean,
    enableCustomerToolsLocationCompetitionNew: boolean,
    enableCustomerToolsLocationExportData: boolean,
    enableCustomerToolsLocationSuggestedNewLocations: boolean,
    enableCustomerToolsLocationFootfallNew: boolean,
    enableCustomerToolsLocationSpendNew: boolean,
    enableCustomerToolsEstate: boolean,
    enableCustomerToolsProduct: boolean,
    enableCustomerToolsProductFootfallNew: boolean,
    enableCustomerToolsReport: boolean,
    enableCustomerToolsReportFootfallNew: boolean,
    enableCustomerToolsLocationRevenuePredictionPlanningData: boolean,
    enableCustomerToolsLocationRetailCentresDirectQuery: boolean,
    enableCustomerToolsLocationRetailers: boolean,
    enableCustomerToolsLocationRetailersAlignment: boolean
}

interface FeatureFlagsState {
    isLoaded: boolean,
    featureFlags: FeatureFlags
}

const initialState: FeatureFlagsState = {
    isLoaded: false,
    featureFlags: {
        enableCustomerToolsLocationDesktop: false,
        enableCustomerToolsLocationMobile: false,
        enableCustomerInsightsPortfolioAreaHealthNew: false,
        enableCustomerInsightsPortfolioCustomCalendar: false,
        enableCustomerInsightsPortfolioFootfallNew: false,
        enableCustomerInsightsPortfolioNearbyCompetitionNew: false,
        enableCustomerInsightsPortfolioNew: false,
        enableCustomerInsightsCost: false,
        enableCustomerInsightsCostCustomCalendar: false,
        enableCustomerHighchartsExporting: false,
        enableCustomerMapboxExporting: false,
        enableCustomerToolsLocationCompetitionNew: false,
        enableCustomerToolsLocationSuggestedNewLocations: false,
        enableCustomerToolsLocationExportData: false,
        enableCustomerToolsLocationFootfallNew: false,
        enableCustomerToolsLocationSpendNew: false,
        enableCustomerToolsEstate: false,
        enableCustomerToolsProduct: false,
        enableCustomerToolsProductFootfallNew: false,
        enableCustomerToolsReport: false,
        enableCustomerToolsReportFootfallNew: false,
        enableCustomerToolsLocationRevenuePredictionPlanningData: false,
        enableCustomerToolsLocationRetailCentresDirectQuery: false,
        enableCustomerToolsLocationRetailers: false,
        enableCustomerToolsLocationRetailersAlignment: false
    }
};

const featureFlagsSlice = createSlice({
    name: "featureFlags",
    initialState,
    reducers: {},
    extraReducers: (builder: any) => {
        builder.addCase(getFeatureFlags.pending, (state: FeatureFlagsState) => {
            state.isLoaded = false;
        });
        builder.addCase(getFeatureFlags.rejected, (state: FeatureFlagsState) => {
            state.isLoaded = true;
        });
        builder.addCase(getFeatureFlags.fulfilled, (state: FeatureFlagsState, action: PayloadAction<FeatureFlags>) => {
            state.isLoaded = true;
            state.featureFlags = action.payload;
        });
    }
});

export const getFeatureFlags = createAppAsyncThunk(
    "featureFlags/getFeatureFlags",
    async (arg, thunkAPI) => {
        const response = await thunkAPI.dispatch(apiGet("/api/feature-flags"));
        if (response.status === ApiResponseStatus.Ok) {
            return response.data;
        }
        return thunkAPI.rejectWithValue(null);
    }
);

export const selectIsFeatureFlagsLoaded = (state: RootState): boolean => {
    return state.featureFlags.isLoaded;
};

export const selectFeatureFlags = (state: RootState) => {
    return state.featureFlags.featureFlags;
};

export default featureFlagsSlice;
